import React, { useState } from 'react';
import { PauseIcon, PlayIcon, StopIcon } from '@heroicons/react/24/solid'; // Changed to Solid for better visibility
import PlayheadTimeline from './PlayheadTimeline';

interface LivePlayTimerCardProps {
  gameName: string;
  elapsedTime: string;
  elapsedSeconds: number;
  expectedDurationMinutes: number | null;
  isPaused: boolean;
  pausedElapsedSeconds: number;
  isParticipant: boolean;
  onPauseResume: () => void;
  onFinish: () => void;
  onDiscard?: () => void;
}

const LivePlayTimerCard: React.FC<LivePlayTimerCardProps> = ({
  gameName,
  elapsedTime,
  elapsedSeconds,
  expectedDurationMinutes,
  isPaused,
  pausedElapsedSeconds,
  isParticipant,
  onPauseResume,
  onFinish,
  onDiscard
}) => {
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);

  return (
    <div className={`mb-6 rounded-2xl p-6 border-2 transition-all duration-500 shadow-sm ${
      isPaused 
        ? 'bg-surface-base border-edge opacity-90' 
        : 'bg-surface border-primary-100 ring-4 ring-primary-50/50'
    }`}>
      
      {/* Top Section: Identity & Live Indicator */}
      <div className="flex justify-between items-start mb-2">
        <div>
          <h3 className="text-xs font-bold text-primary-600 uppercase tracking-widest mb-1">Live Session</h3>
          <h2 className="text-2xl font-black text-heading tracking-tight">{gameName}</h2>
        </div>
        <div className="flex items-center px-3 py-1 bg-green-100 rounded-full">
          <span className={`w-2 h-2 rounded-full mr-2 ${isPaused ? 'bg-gray-400' : 'bg-green-500 animate-pulse'}`} />
          <span className="text-[10px] font-bold text-green-800 uppercase tracking-wider">
            {isPaused ? 'Paused' : 'Active'}
          </span>
        </div>
      </div>

      {/* Hero Timer Section */}
      <div className="flex flex-col items-center my-6">
        <span className={`text-4xl font-mono font-bold tabular-nums transition-colors duration-300 ${
          isPaused ? 'text-disabled' : 'text-heading'
        }`}>
          {elapsedTime}
        </span>
      </div>

      {/* Timeline Section */}
      <div className="px-2 mb-8">
        <PlayheadTimeline
          elapsedSeconds={isPaused ? pausedElapsedSeconds : elapsedSeconds}
          expectedDurationMinutes={expectedDurationMinutes}
          isPaused={isPaused}
        />
      </div>

      {/* Action Row */}
      {isParticipant && (
        <div className="flex flex-col items-center gap-4">
          {!showFinishConfirm ? (
            <div className="flex items-center gap-6">
              {/* Pause/Resume Circle */}
              <button
                onClick={onPauseResume}
                className={`group flex items-center justify-center w-14 h-14 rounded-full transition-all transform active:scale-95 ${
                  isPaused 
                  ? 'bg-green-500 hover:bg-green-600 shadow-lg shadow-green-200' 
                  : 'bg-primary-600 hover:bg-primary-700 shadow-lg shadow-primary-200'
                }`}
              >
                {isPaused ? (
                  <PlayIcon className="h-7 w-7 text-white translate-x-0.5" />
                ) : (
                  <PauseIcon className="h-7 w-7 text-white" />
                )}
              </button>

              {/* Minimalist Finish Button */}
              <button
                onClick={() => setShowFinishConfirm(true)}
                className="flex items-center px-4 py-2 rounded-full border-2 border-red-500 text-sm font-bold text-disabled hover:text-red-500 hover:border-red-600 transition-colors uppercase tracking-widest"
              >
                <StopIcon className="h-4 w-4 mr-1" />
                Finish
              </button>
            </div>
          ) : (
            /* Confirmation State */
            <div className="w-full bg-red-50 border border-red-100 p-4 rounded-xl flex flex-col items-center animate-in fade-in zoom-in duration-200">
              <span className="text-sm font-bold text-red-900 mb-3">End the game session?</span>
              <div className="flex gap-3 w-full">
                <button
                  onClick={onFinish}
                  className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-bold text-sm hover:bg-red-700 shadow-md shadow-red-200"
                >
                  Yes, End Game
                </button>
                <button
                  onClick={() => setShowFinishConfirm(false)}
                  className="flex-1 py-2.5 bg-surface text-body border border-edge rounded-lg font-bold text-sm hover:bg-surface-hover"
                >
                  Cancel
                </button>
              </div>
              {onDiscard && (
                <button
                  onClick={onDiscard}
                  className="mt-3 w-full py-2.5 bg-transparent text-muted border border-edge-input rounded-lg font-medium text-sm hover:bg-surface-active hover:text-body transition-colors"
                >
                  Discard Game
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LivePlayTimerCard;